/**
 * The flashcard surface — phone behaviour (spec §12.5).
 *
 * All session logic lives in `session.ts` as pure functions; this component only
 * renders the current state and forwards intent. Keyboard support exists because
 * the same surface is used at a desk, not because the design targets one.
 */

import { useCallback, useEffect, useState } from "react";
import { RichText } from "../text/RichText";
import { Diagram } from "./Diagram";
import type { Deck } from "./types";
import { answer, currentCard, flip, startSession, stats } from "./session";
import type { Verdict } from "./session";
import "../styles/cards.css";

/** Small caps, like an index card's printed header: the card type, and where it came from. */
function CardHeader({ type, tag, chapter }: { type: string; tag: string | null; chapter: number }) {
  return (
    <span className="card__type">
      <span>{type}</span>
      <span>{tag ?? `chapter ${chapter}`}</span>
    </span>
  );
}

const cards = (n: number) => `${n} ${n === 1 ? "card" : "cards"}`;

export function StudyView({
  deck,
  headingLevel = 1,
  next,
  studied,
  onNext,
  onComplete,
  onWrongPages,
}: {
  deck: Deck;
  /** 2 when this surface is embedded in a page that already owns the <h1>. */
  headingLevel?: 1 | 2;
  /** The book's next chapter, offered at the end; absent on the last. */
  next?: { number: number; title: string; cards: number } | null;
  /** How much of the book has been studied, counting this chapter once it is done. */
  studied?: { done: number; total: number };
  onNext?: () => void;
  /** Once, when every card has been answered "Got it". */
  onComplete?: () => void;
  /** "Wrong pages?": fix this chapter's pages (bug fix 1, spec 7), where a bad boundary shows. */
  onWrongPages?: () => void;
}) {
  const Title = `h${headingLevel}` as "h1" | "h2";
  const [session, setSession] = useState(() => startSession(deck));

  // A new deck is a new session; without this, the old position leaks into it.
  useEffect(() => setSession(startSession(deck)), [deck]);

  const card = currentCard(deck, session);
  const progress = stats(deck, session);

  useEffect(() => {
    if (progress.complete && progress.total > 0) onComplete?.();
    // Fires on the transition to complete only; onComplete is the parent's and may change identity.
  }, [progress.complete]);

  const onFlip = useCallback(() => setSession(flip), []);
  const onAnswer = useCallback((verdict: Verdict) => setSession((s) => answer(s, verdict)), []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!card) return;
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        session.flipped ? onAnswer("known") : onFlip();
      }
      if (session.flipped && (event.key === "1" || event.key === "ArrowLeft")) {
        onAnswer("not-yet");
      }
      if (session.flipped && (event.key === "2" || event.key === "ArrowRight")) {
        onAnswer("known");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, session.flipped, onFlip, onAnswer]);

  return (
    <section className="study" aria-label={`Chapter ${deck.number}: ${deck.title}`}>
      <header className="study__header">
        <div className="study__deck">
          <Title className="study__title">
            {deck.number}. {deck.title}
          </Title>
          <span className="study__count">
            {progress.known} of {progress.total} known
          </span>
          {onWrongPages && (
            <button type="button" className="study__wrong" onClick={onWrongPages}>
              Wrong pages?
            </button>
          )}
        </div>

        {/* One segment per card: covered, not yet, or untouched. Evidence of
            coverage rather than a score (spec §12.4). */}
        <div
          className="coverage"
          role="img"
          aria-label={`${progress.known} of ${progress.total} known`}
        >
          {deck.cards.map((_, i) => {
            const verdict = session.verdicts[i];
            const modifier =
              verdict === "known"
                ? " coverage__segment--known"
                : verdict
                  ? " coverage__segment--not-yet"
                  : "";
            const current = card && session.order[session.position] === i;
            return (
              <div
                className={`coverage__segment${modifier}${current ? " coverage__segment--current" : ""}`}
                key={i}
              />
            );
          })}
        </div>
      </header>

      {card ? (
        <>
          <div className="study__stage">
            {/* A second sheet behind the card: the deck is a stack of paper. */}
            <div className="card__sheet" aria-hidden="true" />
            <button
              // A new card is a new sheet: it arrives face up, never turning back.
              key={session.position}
              type="button"
              className={`card${session.flipped ? " card--turned" : ""}`}
              onClick={onFlip}
              aria-pressed={session.flipped}
              aria-label={session.flipped ? "Show the question" : "Reveal the answer"}
            >
              <span className="card__inner">
                <span className="card__side card__side--front" aria-hidden={session.flipped}>
                  <CardHeader type={card.type} tag={card.tag} chapter={deck.number} />
                  <span className="card__front" data-testid="card-front">
                    <RichText text={card.front} />
                  </span>
                  <span className="card__hint">Tap the card to reveal</span>
                </span>
                {session.flipped && (
                  <span className="card__side card__side--back">
                    <CardHeader type={card.type} tag={card.tag} chapter={deck.number} />
                    <span className="card__prompt" data-testid="card-prompt">
                      <RichText text={card.front} />
                    </span>
                    <span
                      className={`card__back${card.type === "Formula" ? " card__back--formula" : ""}`}
                      data-testid="card-back"
                    >
                      <RichText text={card.back} />
                      {card.mermaid && <Diagram source={card.mermaid} />}
                    </span>
                    <span className="card__disclaimer">
                      Made by AI from your textbook. If it disagrees with the book, the book is
                      right.
                    </span>
                  </span>
                )}
              </span>
            </button>
          </div>

          <p className="study__requeue" aria-live="polite">
            {progress.comingBack > 0 &&
              `${cards(progress.comingBack)} will come back before the end`}
          </p>

          <div className="study__controls">
            {session.flipped ? (
              <>
                <button type="button" className="study__button" onClick={() => onAnswer("not-yet")}>
                  Not yet
                </button>
                <button
                  type="button"
                  className="study__button study__button--known"
                  onClick={() => onAnswer("known")}
                >
                  Got it
                </button>
              </>
            ) : (
              <button
                type="button"
                className="study__button study__button--primary"
                onClick={onFlip}
              >
                Reveal
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="study__done">
          <p className="study__done-kicker">chapter {deck.number} covered</p>
          {/* No confetti, no streak, no score. What it reports is what was actually done. */}
          <h2 className="study__done-title">{cards(progress.total)}, all answered.</h2>
          <p className="study__evidence">
            {progress.cameBack > 0
              ? `${progress.cameBack} came back a second time before they stuck.`
              : "Every card first time."}
          </p>
          {(next || studied) && (
            <dl className="study__facts">
              {next && (
                <div>
                  <dt>Next in this book</dt>
                  <dd>
                    {next.number}. {next.title} · {cards(next.cards)}
                  </dd>
                </div>
              )}
              {studied && (
                <div>
                  <dt>Book so far</dt>
                  <dd>
                    {studied.done} of {studied.total} chapters studied
                  </dd>
                </div>
              )}
            </dl>
          )}
          <div className="study__controls">
            <button
              type="button"
              className="study__button"
              onClick={() => setSession(startSession(deck))}
            >
              Study again
            </button>
            {next && onNext && (
              <button
                type="button"
                className="study__button study__button--primary"
                onClick={onNext}
              >
                Chapter {next.number}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
